import { Children, type PropsWithChildren, type ReactElement, cloneElement, isValidElement, useMemo } from 'react';
import { useDefaultLayout } from 'react-resizable-panels';
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from '@/components/ui/resizable';
import { INSPECTOR_MIN_WIDTH, MAIN_CONTENT_MIN_WIDTH } from '@/components/layouts/panel';
import { useUserSettingsStore } from '@/hooks/data/settings/useUserSettingsStore';
import { useFixedPanelsGroupMinWidth } from '@/hooks/useFixedPanelsGroupMinWidth';
import { type GalleryLayoutProps } from '@/components/layouts/gallery-layout';
import { Inspector } from '@/components/inspector/inspector';
import { type TheNode } from '@/types/tree';

export const IngestionDamLayout = ({
  children,
}: PropsWithChildren<{
  node?: TheNode;
}>) => {
  const showUploadInspector = useUserSettingsStore((s) => s.showUploadInspector);
  const panelIds = useMemo(
    () => ['ingestion-main-content', ...(showUploadInspector ? ['ingestion-inspector'] : [])],
    [showUploadInspector],
  );

  const { defaultLayout, onLayoutChanged } = useDefaultLayout({
    id: 'ingestionLayout',
    panelIds,
    storage: typeof window !== 'undefined' ? localStorage : undefined,
  });

  // Keep the inspector from shrinking when the window narrows; the hook adds the center's floor.
  const { groupRef, minWidth } = useFixedPanelsGroupMinWidth({
    flexPanelId: 'ingestion-main-content',
    minTotalWidth: showUploadInspector ? INSPECTOR_MIN_WIDTH : 0,
    flexPanelMinWidth: MAIN_CONTENT_MIN_WIDTH,
  });

  return (
    <ResizablePanelGroup
      elementRef={groupRef}
      style={{ minWidth }}
      defaultLayout={defaultLayout}
      onLayoutChanged={onLayoutChanged}
    >
      <ResizablePanel
        id="ingestion-main-content"
        minSize={MAIN_CONTENT_MIN_WIDTH}
        className="@container/main relative flex h-full flex-col"
      >
        {Children.map(children, (child) =>
          isValidElement(child) ? cloneElement(child as ReactElement<GalleryLayoutProps>) : child,
        )}
      </ResizablePanel>
      {/* Conditionally rendered (not mounted-then-collapsed) so a v4 relayout can't leave a
          stale grey panel behind. The toolbar toggle drives showUploadInspector. */}
      {showUploadInspector && (
        <>
          <ResizableHandle />
          <ResizablePanel
            id="ingestion-inspector"
            defaultSize="20%"
            minSize={INSPECTOR_MIN_WIDTH}
            // Not collapsible: viewport pressure squishes the inspector but never auto-closes it.
            // Open/close is driven solely by showUploadInspector (toolbar toggle).
            groupResizeBehavior="preserve-pixel-size"
          >
            <Inspector data={[]} isUpload />
          </ResizablePanel>
        </>
      )}
    </ResizablePanelGroup>
  );
};
